import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { logoutHandler } from 'src/consumer/services/loginLogoutHandlers'
import { getStoredAuthorities } from 'src/consumer/services/patientProspectService'

import { ChevronDown } from './icons'
import { Container } from './sections'

// ─────────────────────────────────────────────────────────────────────────────
// AuthenticatedTopBar
//
// Swapped in for the public TopBar (Shell.jsx) whenever the visitor has a
// non-expired auth token. The public TopBar's nav (Doctors / Care-Givers /
// Self-care / Students / For Organizations) is anonymous-discovery copy —
// not what a signed-in patient-prospect needs in their face on every page.
//
// What this surfaces instead:
//   - Context-aware primary nav: My care, Assessments, Help.
//   - Profile chip on the top right with the user's identifier (first name
//     or mobile) and a small dropdown with My care / Assessments / Logout.
//   - Portal switcher between the nav and the profile chip — ONLY shown
//     when the user has more than one accessible portal (e.g. someone who's
//     both a patient-prospect AND a wellness ROLE_USER, or a prospect who
//     later applied as a clinician).
//
// State sources:
//   - ProfileContext.profileData: name (firstName/lastName), mobile, email.
//   - localStorage.authorities: role list, mirrored on every successful auth.
//
// Both are best-effort. If profileData isn't loaded yet, we fall back to
// the stored userName / mobile so the chip never renders blank.
// ─────────────────────────────────────────────────────────────────────────────

// Role → portal mapping. Each entry surfaces in the portal switcher if the
// user holds the matching role AND they have at least one other portal
// available. `external` portals open in a new tab; the rest use the SPA
// router.
const PORTAL_DEFINITIONS = [
  {
    id: 'care',
    label: 'Care',
    sub: 'Your prospect dashboard',
    to: '/care/status',
    roles: ['ROLE_PATIENT_PROSPECT'],
  },
  {
    id: 'patient',
    label: 'Patient home',
    sub: 'Bookings, prescriptions, vitals',
    to: '/patient-home',
    roles: ['ROLE_PATIENT'],
  },
  {
    id: 'wellness',
    label: 'Wellness app',
    sub: 'Meditations, journals, self-care',
    // Canonical wellness home is /wellness now. The old / route still
    // redirects ROLE_USER-only users here via RootLanding, but
    // pointing the switcher at /wellness directly skips the redirect
    // hop and matches the namespaced URL the rest of the portal uses.
    to: '/wellness',
    // ROLE_USER is the baseline AUMHUM identity, so we surface this only
    // when the user has at least one *other* role too — otherwise the
    // switcher would always appear with a single Wellness entry, which
    // is exactly the page they're already on.
    roles: ['ROLE_USER'],
    baseline: true,
  },
  {
    id: 'practitioner',
    label: 'Practitioner home',
    sub: 'Patient queue, calendar, payouts',
    to: '/mentor-home',
    roles: ['ROLE_TEACHER', 'ROLE_THERAPIST', 'ROLE_DOCTOR', 'ROLE_SCREENER'],
  },
  {
    id: 'practitioner-applicant',
    label: 'Practitioner application',
    sub: 'Finish your onboarding',
    to: '/mentor-home/onboarding/stage2',
    // ROLE_CREATOR is the in-progress applicant role. Only surface it
    // when a higher practitioner role isn't already present (otherwise
    // "application" sits next to the active practitioner portal, which
    // is confusing).
    roles: ['ROLE_CREATOR'],
    hideIfAnyOf: ['ROLE_TEACHER', 'ROLE_THERAPIST', 'ROLE_DOCTOR', 'ROLE_SCREENER'],
  },
  {
    id: 'support',
    label: 'Support portal',
    sub: 'Prospect queue, audits, ops',
    to: '/support-home',
    // Comprehensive list of internal-staff roles — mirrors
    // mentor/layout/utils.getAllRolesInBoolean. Without all of these
    // listed, a support agent (e.g. ROLE_THERAPY_SUP_L1, ROLE_DOCSIDE_ADMIN,
    // ROLE_VC_AUDIT_MANAGER) didn't see "Support portal" in the
    // header switcher even though /support-home is where they live.
    roles: [
      // Admin tier
      'ROLE_ADMIN',
      'ROLE_SUB_ADMIN',
      'ROLE_APP_MANAGER',
      'ROLE_DEVELOPER',
      'ROLE_MANAGEMENT',
      // Therapy support
      'ROLE_THERAPY_SUP_L1',
      'ROLE_THERAPY_SUP_L2',
      'ROLE_THERAPY_SUP_L3',
      'ROLE_THERAPIST_ADMIN',
      // Roster + success management
      'ROLE_CLINIC_ROSTER_MANAGER',
      'ROLE_CLINIC_ROSTER_ADMIN',
      'ROLE_CLINIC_SUCCESS_MANAGER',
      'ROLE_CLINIC_SUCCESS_ADMIN',
      // Audit + docside
      'ROLE_VC_AUDIT_ADMIN',
      'ROLE_VC_AUDIT_MANAGER',
      'ROLE_DOCSIDE_ADMIN',
      'ROLE_DOCSIDE_MANAGER',
      // Generic support tiers
      'ROLE_SUPPORT',
      'ROLE_SUPPORT_USER',
      'ROLE_PAYMENT_SUPPORT',
      // Restricted-read (still a staff role — same destination)
      'ROLE_READ_ONLY_RESTRICTED',
    ],
  },
  {
    id: 'clinic-home',
    label: 'Clinic admin',
    sub: 'People, hours, payouts',
    // The clinic portal lives at /clinic-home, NOT /support-home — an
    // earlier version of this entry pointed at /support-home which was
    // a no-op landing for clinic operators.
    to: '/clinic-home',
    roles: [
      'ROLE_CLINIC_OWNER',
      'ROLE_CLINIC_MANAGER',
      'ROLE_CLINIC_ADMIN',
      'ROLE_CLINIC_PROSPECT',
      'ROLE_PHARMACIST',
    ],
  },
  {
    id: 'business',
    label: 'Workplace portal',
    sub: 'Organisation wellness, members, reports',
    to: '/workplace',
    roles: ['ROLE_BUSINESS_CUSTOMER', 'ROLE_BUSINESS_PROSPECT'],
  },
  {
    id: 'clinic-assistant',
    label: 'Clinic assistant',
    sub: 'Walk-ins, screening, queueing',
    to: '/ca-home',
    roles: ['ROLE_CLINIC_ASSISTANT'],
  },
  {
    id: 'aumhum-screen',
    label: 'AumHum screen',
    sub: 'Kiosk session screen',
    to: '/kiosk',
    roles: ['ROLE_AUMHUM_SCREEN'],
  },
  {
    id: 'chamber-screen',
    label: 'Chamber screen',
    sub: 'In-chamber screen surface',
    to: '/chamber-screen-home',
    roles: ['ROLE_CHAMBER_SCREEN'],
  },
]

function resolvePortals(authorities) {
  if (!Array.isArray(authorities) || authorities.length === 0) return []
  const has = (role) => authorities.includes(role)
  const matched = []
  for (const portal of PORTAL_DEFINITIONS) {
    const matches = portal.roles.some(has)
    if (!matches) continue
    if (portal.hideIfAnyOf && portal.hideIfAnyOf.some(has)) continue
    matched.push(portal)
  }
  // Drop the wellness baseline when it's the only portal — otherwise the
  // switcher would show "Wellness app" alone, which is just the home page.
  if (matched.length === 1 && matched[0].baseline) return []
  return matched
}

function deriveDisplayName(profileData, authorities) {
  if (profileData) {
    const first = (profileData.firstName || '').trim()
    const last = (profileData.lastName || '').trim()
    if (first || last) {
      return [first, last].filter(Boolean).join(' ')
    }
    if (profileData.userName) return profileData.userName
    if (profileData.mobile) return profileData.mobile
    if (profileData.email) return profileData.email
  }
  // Auth token but no profile loaded yet — fall back to whatever
  // identifier we have in localStorage so the chip doesn't render blank
  // during the brief load window.
  try {
    const u = window.localStorage.getItem('username')
    if (u) return u
  } catch {
    // ignore
  }
  // Last-resort label: the first non-baseline role they have, lower-cased
  // and humanised, so the chip says something rather than "Account".
  const portalRole = (authorities || []).find((r) => r && r !== 'ROLE_USER')
  if (portalRole) {
    return portalRole
      .replace(/^ROLE_/, '')
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }
  return 'Account'
}

function initialsFor(name) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// ─── Small dropdown wrapper with click-outside + Esc to close ────────────────
function HeaderDropdown({ anchor, panelClassName = '', children, align = 'right', wrapClassName = 'as-auth-dd' }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div
      className={wrapClassName}
      data-open={open}
      data-align={align}
      ref={wrapRef}
    >
      {anchor({ open, toggle: () => setOpen((o) => !o), close: () => setOpen(false) })}
      {open ? (
        <div className={`as-auth-dd-panel ${panelClassName}`.trim()} role="menu">
          {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
        </div>
      ) : null}
    </div>
  )
}

// ─── Authenticated TopBar ────────────────────────────────────────────────────
export default function AuthenticatedTopBar({ contact, onContactClick, scrolled, onSwitchToPublicNav }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const profileContext = useContext(ProfileContext)
  const profileData = profileContext?.profileData
  // Authorities can change without a route navigation (e.g. /initialize
  // re-issuing a JWT with ROLE_PATIENT_PROSPECT added). Resync on the
  // same signals the public-side useIsAuthenticated listens to.
  const [authorities, setAuthorities] = useState(getStoredAuthorities)
  useEffect(() => {
    const refresh = () => setAuthorities(getStoredAuthorities())
    refresh()
    window.addEventListener('storage', refresh)
    window.addEventListener('aumhum:auth-changed', refresh)
    return () => {
      window.removeEventListener('storage', refresh)
      window.removeEventListener('aumhum:auth-changed', refresh)
    }
  }, [location.pathname])

  // Close mobile drawer on route change so navigating away from the
  // current page doesn't leave the menu open over the next screen.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const portals = useMemo(() => resolvePortals(authorities), [authorities])
  const displayName = useMemo(
    () => deriveDisplayName(profileData, authorities),
    [profileData, authorities],
  )
  const initials = useMemo(() => initialsFor(displayName), [displayName])

  const handleLogout = () => {
    // logoutHandler clears localStorage / sessionStorage + Firebase auth
    // + Redux stores, then dispatches `aumhum:auth-changed` so every
    // auth-aware component re-renders. We hard-navigate to the public
    // home after so the user lands on the marketing surface logged out.
    try {
      logoutHandler()
    } catch (e) {
      console.warn('logout encountered an error (continuing):', e?.message || e)
    }
    // Use window.location.assign so the auth-aware Shell re-mounts and
    // any role-gated routes (Redux, contexts) reset cleanly.
    window.location.assign('/')
  }

  return (
    <header
      className="as-topbar as-topbar--authed"
      role="banner"
      data-scrolled={scrolled ? 'true' : 'false'}
    >
      <Container>
        <div className="as-topbar-inner">
          <Link to="/care/status" className="as-brand" aria-label="AUMHUM — your care">
            <span className="as-brand-mark" aria-hidden="true">
              <img
                src={APP_CONSTANTS.APP_LOGO_LINK}
                alt=""
                loading="eager"
                decoding="async"
                width={32}
                height={32}
              />
            </span>
            <span>AUMHUM</span>
          </Link>

          {/* Context-aware nav. Items here target what a signed-in user
              actually does post-signup, not the public discovery copy
              (Doctors / Care-Givers / Self-care). */}
          <nav className="as-nav as-nav--authed" aria-label="Primary">
            <Link to="/care/status" data-active={location.pathname.startsWith('/care/status') ? 'true' : 'false'}>
              My care
            </Link>
            {/* Point in-app nav directly at /wellness/assessment so the
                authenticated user lands on the namespaced URL without
                the /assessment → /wellness/assessment redirect hop. The
                public /assessment route still works for anonymous /
                SEO traffic. */}
            <Link
              to="/wellness/assessment"
              data-active={
                location.pathname.startsWith('/wellness/assessment')
                  || location.pathname.startsWith('/assessment')
                  ? 'true' : 'false'
              }
            >
              Assessments
            </Link>
            <Link to="/how-care-works">How care works</Link>
          </nav>

          <div className="as-topbar-actions">
            {contact ? (
              <button
                type="button"
                className="as-header-phone"
                onClick={onContactClick}
                aria-label={`Call AUMHUM at ${contact.display}`}
                title="Connect with our team"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span className="as-header-phone-num">{contact.display}</span>
              </button>
            ) : null}

            {/* Portal switcher — only when more than one accessible portal
                exists. Carries a chevron + the current portal label so
                a multi-role user sees at a glance which surface they're
                currently on. */}
            {portals.length > 1 ? (
              <HeaderDropdown
                wrapClassName="as-auth-dd as-portal-switcher"
                align="right"
                anchor={({ open, toggle }) => (
                  <button
                    type="button"
                    className="as-portal-switcher-btn"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={toggle}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                    <span className="as-portal-switcher-label">Switch portal</span>
                    <ChevronDown />
                  </button>
                )}
              >
                {({ close }) => (
                  <div className="as-auth-dd-card as-portal-switcher-card" role="menu">
                    <div className="as-auth-dd-eyebrow">Your portals</div>
                    {portals.map((portal) => (
                      <button
                        key={portal.id}
                        type="button"
                        role="menuitem"
                        className="as-auth-dd-item"
                        onClick={() => {
                          close()
                          // Wellness portal needs an explicit "user
                          // picked this" flag so ConsumerHomepage's
                          // auto-routing on mount doesn't bounce a
                          // multi-role visitor (prospect/patient +
                          // ROLE_USER) back to /care/status or
                          // /patient-home. Without the flag,
                          // ConsumerHomepage treats the visit as a
                          // first-time entry and re-runs its
                          // best-portal selection, which for a
                          // prospect picks /care/status — exactly the
                          // surface they were trying to leave.
                          //
                          // Also clear the "auto-navigated this
                          // session" flag so the wellness flow
                          // selection / onboarding renders fresh.
                          if (portal.id === 'wellness') {
                            try {
                              window.sessionStorage.setItem(
                                'explicitlyChoseConsumerPortal',
                                'true',
                              )
                              window.sessionStorage.removeItem(
                                'portalAutoNavigatedToAtFlow',
                              )
                              window.localStorage.setItem('lastChosenFlow', 'consumer')
                            } catch {
                              // sessionStorage failures are non-fatal — the
                              // gate above still renders the wellness UI,
                              // just without the flag the page-internal
                              // auto-router might tweak the landing tile.
                            }
                          }
                          // Mix of in-app and external destinations. Both
                          // /patient-home/* and /mentor-home/* are inside
                          // this SPA — react-router handles them; only the
                          // external Control Center URL would need
                          // window.location, and we don't surface that
                          // here yet.
                          navigate(portal.to)
                        }}
                      >
                        <div className="as-auth-dd-item-label">{portal.label}</div>
                        {portal.sub ? <div className="as-auth-dd-item-sub">{portal.sub}</div> : null}
                      </button>
                    ))}
                  </div>
                )}
              </HeaderDropdown>
            ) : null}

            {/* Profile chip — initials avatar + name. The chip itself is
                the dropdown trigger so it never feels like dead weight. */}
            <HeaderDropdown
              wrapClassName="as-auth-dd as-profile-dd"
              align="right"
              anchor={({ open, toggle }) => (
                <button
                  type="button"
                  className="as-profile-chip"
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={toggle}
                  title={displayName}
                >
                  <span className="as-profile-avatar" aria-hidden="true">{initials}</span>
                  <span className="as-profile-name">{displayName}</span>
                  <ChevronDown />
                </button>
              )}
            >
              {({ close }) => (
                <div className="as-auth-dd-card as-profile-card" role="menu">
                  <div className="as-profile-card-header">
                    <span className="as-profile-avatar as-profile-avatar--lg" aria-hidden="true">{initials}</span>
                    <div className="as-profile-card-meta">
                      <div className="as-profile-card-name">{displayName}</div>
                      {profileData?.mobile ? (
                        <div className="as-profile-card-sub">{profileData.mobile}</div>
                      ) : profileData?.email ? (
                        <div className="as-profile-card-sub">{profileData.email}</div>
                      ) : null}
                    </div>
                  </div>
                  <Link to="/care/status" className="as-auth-dd-item" role="menuitem" onClick={close}>
                    <div className="as-auth-dd-item-label">My care status</div>
                  </Link>
                  <Link to="/wellness/assessment" className="as-auth-dd-item" role="menuitem" onClick={close}>
                    <div className="as-auth-dd-item-label">Self-assessments</div>
                  </Link>
                  {onSwitchToPublicNav ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="as-auth-dd-item"
                      onClick={() => {
                        close()
                        onSwitchToPublicNav()
                      }}
                    >
                      <div className="as-auth-dd-item-label">Browse public site</div>
                      <div className="as-auth-dd-item-sub">Doctors, care-givers, self-care</div>
                    </button>
                  ) : null}
                  <div className="as-auth-dd-divider" role="separator" />
                  <button
                    type="button"
                    role="menuitem"
                    className="as-auth-dd-item as-auth-dd-item--danger"
                    onClick={() => {
                      close()
                      handleLogout()
                    }}
                  >
                    <div className="as-auth-dd-item-label">Log out</div>
                  </button>
                </div>
              )}
            </HeaderDropdown>

            <button
              type="button"
              className="as-mobile-toggle as-login-btn"
              aria-expanded={mobileOpen}
              aria-controls="aumhum-mobile-menu-authed"
              aria-label="Toggle navigation"
              onClick={() => setMobileOpen((m) => !m)}
            >
              Menu
            </button>
          </div>
        </div>

        {mobileOpen ? (
          <nav
            id="aumhum-mobile-menu-authed"
            className="as-mobile-menu as-mobile-menu--authed"
            aria-label="Mobile"
            onClick={(e) => {
              if (e.target.closest && e.target.closest('a, button[data-mobile-close]')) {
                setMobileOpen(false)
              }
            }}
          >
            <div className="as-mobile-profile-header">
              <span className="as-profile-avatar as-profile-avatar--lg" aria-hidden="true">{initials}</span>
              <div className="as-profile-card-meta">
                <div className="as-profile-card-name">{displayName}</div>
                {profileData?.mobile ? (
                  <div className="as-profile-card-sub">{profileData.mobile}</div>
                ) : null}
              </div>
            </div>
            <Link to="/care/status">My care</Link>
            <Link to="/wellness/assessment">Self-assessments</Link>
            <Link to="/how-care-works">How care works</Link>

            {portals.length > 1 ? (
              <details className="as-mobile-portals">
                <summary>Switch portal</summary>
                <div>
                  {portals.map((p) => (
                    <Link
                      key={p.id}
                      to={p.to}
                      onClick={() => {
                        // Same explicit-choice flag as the desktop
                        // switcher — keeps ConsumerHomepage's
                        // auto-router from bouncing a multi-role
                        // visitor back to /care/status when they
                        // intentionally pick the wellness portal.
                        if (p.id === 'wellness') {
                          try {
                            window.sessionStorage.setItem(
                              'explicitlyChoseConsumerPortal',
                              'true',
                            )
                            window.sessionStorage.removeItem(
                              'portalAutoNavigatedToAtFlow',
                            )
                            window.localStorage.setItem('lastChosenFlow', 'consumer')
                          } catch {
                            // non-fatal
                          }
                        }
                      }}
                    >
                      {p.label}
                    </Link>
                  ))}
                </div>
              </details>
            ) : null}

            <button
              type="button"
              className="as-mobile-logout"
              data-mobile-close="true"
              onClick={handleLogout}
            >
              Log out
            </button>
          </nav>
        ) : null}
      </Container>
    </header>
  )
}
